/**
 * 用户身份枚举类型
 */
export type UserIdentity = 'freeVip' | 'basicVip' | 'advancedVip' | 'enterpriseVip';

/**
 * 商品相关字段
 */
export enum CommodityDate {
  'YEAR' = 5,
  'MONTH' = 2,
  'TWOWEEK' = 1,
}
export enum CommodityCurrency {
  'CNY' = 'CNY',
  'USD' = 'USD',
}
export type DurationType =
  | CommodityDate.YEAR
  | CommodityDate.MONTH
  | CommodityDate.TWOWEEK;
export type CurrencyType = CommodityCurrency.CNY | CommodityCurrency.USD;

export type Commodity = Array<{
  commodityCode: string;
  title: string;
  desc: string;
  modelCode: string;
  originalPrice: number;
  realPrice: number;
  discount: number;
  sort: number;
  commodityType: number;
  increaseTime: number;
  content: {
    durationType: DurationType;
    category: UserIdentity;
    point: number;
    label: string;
    currency: CurrencyType;
    commodityName: string;
  };
}>;

export enum FunctionNameEnum {
  F_SINGLE_IMG_TRANS = 'fSingleImgTrans',
  F_TEXT_TO_SPEECH = 'fTextToSpeech',
  F_VOICE_CLONE = 'fVoiceClone',
  F_IMAGE_GENERATE = 'fimageGenerate',
  F_VIDEO_GENERATE = 'fVideoGenerate',
  F_PRODUCT_AVATAR = 'fProductAvatar',
  F_LIP_SYNC_V2 = 'fLipSyncV2',
  F_AUDIO_TRANSLATE = 'fAudioTranslate',
  F_SUBTITLE_TRANSLATE = 'fSubtitleTranslate',
  F_HEAD_SWAP = 'fHeadSwap',
  F_VIDEO_FACE_MERGE = 'fVideoFaceMerge',
  F_IMG_UPSCALER = 'fImgUpscaler',
  F_CAPTION_CLEAR_OFF = 'fCaptionClearoff',
  F_VIDEO_EDIT = 'fVideoEdit',
  F_VIRAL_REMAKE = 'fViraRemake',
}

/**
 * 功能名称
 */
export type FunctionName =
  | 'subtitleTranslate'
  | 'audioTranslate'
  | 'digitalHumanVideo'
  | 'videoFaceMerge'
  | 'subtitleRemove'
  | 'removeWaterMark'
  | 'textEdit'
  | 'lipSync'
  | 'uploadResolution'
  | 'exportResolution'
  | 'duration'
  | 'batchNum'
  | 'weight'
  | 'digitalHumanNum'
  | 'voiceCloneNum'
  | 'assetAvailablePeriod'
  | 'cloudStorageCapacityMB'
  | 'shortMovieCommentate'
  | 'voiceChanging'
  | FunctionNameEnum;

/**
 * 分辨率限制
 */
export type Resolution = 480 | 720 | 1080 | 2160;
/**
 * 分辨率限制业务层输出
 */
export type ResolutionOutput = '480p' | '720p' | '1080p' | '4k';

/**
 * TODO: 后续可能会增加动态配置，目前所有用户共享一份价格配置，不区分用户类型
 * 价格配置
 */
export const priceConfig = {
  // 字幕擦除 3c/min
  subtitleRemove: 3,
  // 字幕翻译 2c/min/language
  textTranslation: 2,
  // tts语音生成 4c/min
  textToSpeech: 4,
  // 唇型同步 4c/min
  avatarVideo: 4,
  // 换脸 4c/min
  faceSwap: 5,
};

/**
 * 功能点价格配置 & 计算
 */
export const functionPriceConfig = {
  /**
   * 字幕擦除
   */
  subtitleRemove: {
    keys: ['subtitleRemove'],
    computed: (duration: number) => {
      const price = duration * priceConfig.subtitleRemove;
      return {
        price,
        detail: [
          {
            key: 'subtitleRemove',
            price,
          },
        ],
      };
    },
  },
  /**
   * 字幕翻译
   */
  subtitleTranslate: {
    keys: ['subtitleRemove', 'textTranslation'],
    computed: (duration: number, languageNum: number = 1) => {
      const subtitleRemovePrice = duration * priceConfig.subtitleRemove;
      const textTranslationPrice = duration * priceConfig.textTranslation * languageNum;
      return {
        price: subtitleRemovePrice + textTranslationPrice,
        detail: [
          {
            key: 'subtitleRemove',
            price: subtitleRemovePrice,
          },
          {
            key: 'textTranslation',
            price: textTranslationPrice,
          },
        ],
      };
    },
  },
  /**
   * 音频翻译
   */
  audioTranslate: {
    keys: ['textToSpeech'],
    computed: (duration: number, withAvatar: boolean) => {
      const textToSpeechPrice = duration * priceConfig.textToSpeech;
      const avatarVideoPrice = withAvatar ? duration * priceConfig.avatarVideo : 0;
      let detail = [
        {
          key: 'textToSpeech',
          price: textToSpeechPrice,
        },
      ];
      if (withAvatar) {
        detail.push({
          key: 'avatarVideo',
          price: avatarVideoPrice,
        });
      }
      return {
        price: textToSpeechPrice + avatarVideoPrice,
        detail,
      };
    },
  },
  /**
   * 数字人视频
   */
  digitalHumanVideo: {
    keys: ['avatarVideo'],
    computed: (duration: number) => {
      const price = duration * priceConfig.avatarVideo;
      return {
        price,
        detail: [
          {
            key: 'avatarVideo',
            price,
          },
        ],
      };
    },
  },
  /**
   * 视频换脸
   */
  videoFaceMerge: {
    keys: ['faceSwap'],
    computed: (duration: number) => {
      const price = duration * priceConfig.faceSwap;
      return {
        price,
        detail: [
          {
            key: 'faceSwap',
            price,
          },
        ],
      };
    },
  },
  /**
   * 视频翻译对口型
   */
  lipSync: {
    keys: ['avatarVideo'],
  },
};

export const commodityCata = {
  commodityCode: '',
  title: '',
  desc: '',
  modelCode: '',
  originalPrice: 1,
  realPrice: 1,
  discount: 0,
  sort: 1,
  commodityType: 1,
  increaseTime: 1,
  content: {
    durationType: 2,
    category: '',
    point: 1,
    label: '',
    currency: '',
  },
};
